// Scholar Sync - Citation Management for Word
// Main entry point for taskpane functionality

/* global console */

import { insertCitation, findAllCitations, insertBibliography } from "../services/word";
import { formatReference } from "../services/citation";
import { mockPapers } from "../data/mockPapers";
import { CitationStyle, Paper } from "../types/paper";

// Export the citation functions for use in React components
export { insertCitation, findAllCitations, insertBibliography };

// Global papers cache for bibliography generation
let papersCache: Paper[] = mockPapers;

/**
 * Set the papers cache for bibliography generation
 */
export function setPapersCache(papers: Paper[]): void {
  papersCache = papers;
  console.log(`✅ Papers cache updated with ${papers.length} papers`);
}

/**
 * Generate and insert bibliography based on citations in document
 */
export async function generateBibliography(style: CitationStyle = "APA"): Promise<void> {
  try {
    // Find all citations in document
    const paperIds = await findAllCitations();

    if (paperIds.length === 0) {
      console.log("No citations found in document");
      throw new Error("No citations found in document. Insert citations first.");
    }

    // Get unique paper IDs
    const uniquePaperIds = Array.from(new Set(paperIds));
    console.log(`Found ${uniquePaperIds.length} unique citations:`, uniquePaperIds);

    // Format references using papers cache
    const references = uniquePaperIds
      .map((paperId) => {
        const paper = papersCache.find((p) => p.id === paperId);
        if (paper) {
          console.log(`Formatting reference for: ${paper.title}`);
          return formatReference(paper, style);
        } else {
          console.warn(`Paper not found in cache: ${paperId}`);
        }
        return null;
      })
      .filter((ref) => ref !== null) as string[];

    if (references.length === 0) {
      throw new Error("Could not find any papers for the citations in document.");
    }

    // Sort references alphabetically
    references.sort();

    console.log(`Generated ${references.length} references:`, references);

    // Insert bibliography
    await insertBibliography(references);

    console.log(`✅ Bibliography generated with ${references.length} references`);
  } catch (error) {
    console.error("❌ Error generating bibliography:", error);
    throw error;
  }
}
